<template>
  <template v-if="order">
    <template v-if="!order.invoice?.isPaid">
      <section class="mb-8">
        <div
          class="bg-error/10 border-error text-error border-1 mb-4 rounded-lg px-6 py-3 text-center font-semibold"
        >
          Payment Incomplete
        </div>
      </section>
    </template>
    <section class="mb-8">
      <div class="bg-background rounded-lg p-4">
        <dl class="space-y-1">
          <div>
            <dt class="text-background-fg/70 leading-loose">
              Total
            </dt>
            <dd class="text-3xl font-bold">
              {{
                formatToMerchantCurrencyString(
                  order.totalCost
                )
              }}
            </dd>
          </div>
        </dl>
        <div class="bg-background-fg/8 my-2 h-px w-full" />
        <dl class="space-y-1">
          <div class="flex items-center justify-between">
            <dt
              v-if="order.orderNumber !== null"
              class="text-background-fg/70 text-xs leading-loose"
            >
              Order No.
            </dt>
            <dd class="text-sm font-semibold">
              #{{ order.orderNumber }}
            </dd>
          </div>
          <div class="flex items-center justify-between">
            <dt
              class="text-background-fg/70 text-xs leading-loose"
            >
              Date
            </dt>
            <dd class="text-sm font-semibold">
              {{
                formatToDate(
                  order.invoice?.paidAt ?? order.createdAt
                )
              }}
            </dd>
          </div>
          <div class="flex items-center justify-between">
            <dt
              class="text-background-fg/70 text-xs leading-loose"
            >
              Time
            </dt>
            <dd class="text-sm font-semibold">
              {{
                formatToTime(
                  order.invoice?.paidAt ?? order.createdAt
                )
              }}
            </dd>
          </div>
          <template v-if="order.cashierId">
            <div class="flex items-center justify-between">
              <dt
                class="text-background-fg/70 text-xs leading-loose"
              >
                Cashier
              </dt>
              <dd class="text-sm font-semibold">
                {{ cashier?.username }}
              </dd>
            </div>
          </template>
          <OrderDetailReceipt
            v-if="order.invoice?.isPaid"
            :order-id="orderId"
          />
        </dl>
      </div>
    </section>

    <section class="mb-8">
      <h3 class="text-background-fg/70 mb-3 text-sm">
        Payments
      </h3>
      <div class="bg-background rounded-lg p-4">
        <ol>
          <template
            v-for="transaction in order.payments"
            :key="transaction.id"
          >
            <OrderDetailTransactionItem
              :transaction="transaction"
              @click-transaction="onClickTransaction"
            />
          </template>
        </ol>
      </div>
    </section>

    <section>
      <h3 class="text-background-fg/70 mb-3 text-sm">
        Items
      </h3>
      <div class="bg-background rounded-lg p-4">
        <ul class="space-y-2">
          <template
            v-for="orderItem in order.orderItems"
            :key="orderItem.id"
          >
            <OrderDetailLineItem :order-item="orderItem" />
          </template>
        </ul>
      </div>
    </section>
  </template>

  <TransactionPopup
    v-if="currentTransaction"
    :is-open="isTransactionDrawerOpen"
    :transaction="currentTransaction"
    @close="handleCloseTransactionDrawer"
  />
</template>

<script setup lang="ts">
import type {
  TransactionDetail,
  Refund,
} from "@/services/sharedTypes/transactions"
const { getById: getCashierById } = useCashiers()
const { formatToMerchantCurrencyString } = useCurrency()

const props = defineProps<{
  orderId: string
  previousPage?: string
}>()

const { data: order } = useOrder(props.orderId)

const cashier = computed(
  () => getCashierById(order.value?.cashierId).value
)

const currentTransaction = ref<TransactionDetail | Refund>()
const isTransactionDrawerOpen = ref(false)
const showTransactionDrawer = () => {
  isTransactionDrawerOpen.value = true
}
const handleCloseTransactionDrawer = () => {
  isTransactionDrawerOpen.value = false
}

const onClickTransaction = (
  transaction: TransactionDetail | Refund
) => {
  if (
    transaction.status === "cancelled" ||
    transaction.status === "pending"
  )
    return

  currentTransaction.value = transaction
  showTransactionDrawer()
}
</script>
